import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Clock, 
  ArrowRight,
  Calendar,
  Users,
  MessageSquare,
  ListTodo,
  Glasses,
  Waves,
  BookOpen,
  GraduationCap
} from "lucide-react";

interface PlatformCard {
  name: string;
  tagline: string;
  shutdownDate: string;
  shutdownYear: string;
  description: string;
  alternativeUrl: string;
  historyUrl: string;
  icon: React.ReactNode;
  color: string;
}

const Alternatives = () => {
  const platforms: PlatformCard[] = [
    {
      name: "Groove",
      tagline: "Virtual Coworking Platform",
      shutdownDate: "January 2025",
      shutdownYear: "2025",
      description: "The pandemic-era coworking app that created a movement of Groovers beating procrastination together through virtual accountability sessions.",
      alternativeUrl: "/groove/",
      historyUrl: "/groove/history/",
      icon: <Users className="w-6 h-6" />,
      color: "bg-blue-500/10 text-blue-500"
    },
    {
      name: "Studyverse",
      tagline: "Virtual Study Rooms",
      shutdownDate: "2024",
      shutdownYear: "2024",
      description: "A beloved virtual study platform that offered themed study rooms and community features for students worldwide.",
      alternativeUrl: "/studyverse/",
      historyUrl: "/studyverse/history/",
      icon: <BookOpen className="w-6 h-6" />,
      color: "bg-purple-500/10 text-purple-500"
    },
    {
      name: "Edmodo",
      tagline: "Classroom Social Network",
      shutdownDate: "September 2022",
      shutdownYear: "2022",
      description: "The 'Facebook of Education' that connected 100 million users before its sudden shutdown, leaving educators searching for alternatives.",
      alternativeUrl: "/edmodo/",
      historyUrl: "/edmodo/history/",
      icon: <GraduationCap className="w-6 h-6" />,
      color: "bg-orange-500/10 text-orange-500"
    },
    {
      name: "OpenStudy",
      tagline: "Peer Learning Platform",
      shutdownDate: "2015",
      shutdownYear: "2015",
      description: "A pioneering peer-to-peer learning platform that helped 50 million learners before its acquisition by Brainly.",
      alternativeUrl: "/openstudy/",
      historyUrl: "/openstudy/history/",
      icon: <MessageSquare className="w-6 h-6" />,
      color: "bg-green-500/10 text-green-500"
    },
    {
      name: "Wunderlist",
      tagline: "To-Do List App",
      shutdownDate: "May 6, 2020",
      shutdownYear: "2020",
      description: "The beloved task management app by 6Wunderkinder that Microsoft acquired and shut down in favor of Microsoft To Do.",
      alternativeUrl: "/wunderlist/",
      historyUrl: "/wunderlist/history/",
      icon: <ListTodo className="w-6 h-6" />,
      color: "bg-red-500/10 text-red-500"
    },
    {
      name: "AltspaceVR",
      tagline: "Social VR Platform",
      shutdownDate: "March 10, 2023",
      shutdownYear: "2023",
      description: "A pioneering social VR platform where users gathered as avatars for comedy shows, concerts, and educational sessions before Microsoft shut it down.",
      alternativeUrl: "/altspacevr/",
      historyUrl: "/altspacevr/history/",
      icon: <Glasses className="w-6 h-6" />,
      color: "bg-cyan-500/10 text-cyan-500"
    },
    {
      name: "Google Wave",
      tagline: "Collaboration Platform",
      shutdownDate: "April 30, 2012",
      shutdownYear: "2012",
      description: "An ahead-of-its-time collaboration tool that combined email, IM, and document editing. Its innovations live on in Google Docs and Slack.",
      alternativeUrl: "/google-wave/",
      historyUrl: "/google-wave/history/",
      icon: <Waves className="w-6 h-6" />,
      color: "bg-yellow-500/10 text-yellow-500"
    },
    {
      name: "HipChat",
      tagline: "Team Chat App",
      shutdownDate: "February 15, 2019",
      shutdownYear: "2019",
      description: "Atlassian's pioneering team chat that helped define workplace messaging before losing to Slack and being discontinued.",
      alternativeUrl: "/hipchat/",
      historyUrl: "/hipchat/history/",
      icon: <MessageSquare className="w-6 h-6" />,
      color: "bg-indigo-500/10 text-indigo-500"
    },
    {
      name: "Stride",
      tagline: "Team Messaging",
      shutdownDate: "February 15, 2019",
      shutdownYear: "2019",
      description: "Atlassian's ambitious HipChat successor with Focus Mode and Actions tracking that lasted less than two years.",
      alternativeUrl: "/stride/",
      historyUrl: "/stride/history/",
      icon: <Waves className="w-6 h-6" />,
      color: "bg-violet-500/10 text-violet-500"
    },
    {
      name: "Sunrise Calendar",
      tagline: "Calendar App",
      shutdownDate: "August 31, 2016",
      shutdownYear: "2016",
      description: "The 'best calendar app ever' that Microsoft acquired for $100 million and shut down to integrate into Outlook.",
      alternativeUrl: "/sunrise-calendar/",
      historyUrl: "/sunrise-calendar/history/",
      icon: <Calendar className="w-6 h-6" />,
      color: "bg-amber-500/10 text-amber-500"
    },
    {
      name: "Mailbox",
      tagline: "Email App",
      shutdownDate: "February 26, 2016",
      shutdownYear: "2016",
      description: "The innovative email app that introduced swipe gestures and snoozing, acquired by Dropbox for $100 million.",
      alternativeUrl: "/mailbox/",
      historyUrl: "/mailbox/history/",
      icon: <MessageSquare className="w-6 h-6" />,
      color: "bg-sky-500/10 text-sky-500"
    },
    {
      name: "Yahoo Groups",
      tagline: "Online Community Platform",
      shutdownDate: "December 15, 2020",
      shutdownYear: "2020",
      description: "The internet's first social network that hosted millions of communities before Yahoo shut it down after 25 years of service.",
      alternativeUrl: "/yahoo-groups/",
      historyUrl: "/yahoo-groups/history/",
      icon: <Users className="w-6 h-6" />,
      color: "bg-purple-600/10 text-purple-600"
    },
    {
      name: "Google Spaces",
      tagline: "Group Sharing App",
      shutdownDate: "April 17, 2017",
      shutdownYear: "2017",
      description: "Google's experiment in group messaging and content sharing that lasted just 10 months before being discontinued.",
      alternativeUrl: "/google-spaces/",
      historyUrl: "/google-spaces/history/",
      icon: <MessageSquare className="w-6 h-6" />,
      color: "bg-blue-600/10 text-blue-600"
    },
    {
      name: "Quill",
      tagline: "Team Chat Platform",
      shutdownDate: "2015",
      shutdownYear: "2015",
      description: "A pioneering team messaging platform that merged with Slack, bringing innovative chat features to modern workplace communication.",
      alternativeUrl: "/quill/",
      historyUrl: "/quill/history/",
      icon: <MessageSquare className="w-6 h-6" />,
      color: "bg-teal-500/10 text-teal-500"
    },
    {
      name: "Google Hangouts",
      tagline: "Video Chat Platform",
      shutdownDate: "November 1, 2022",
      shutdownYear: "2022",
      description: "Google's unified messaging platform that brought video calls to the masses before transitioning to Google Chat and Meet.",
      alternativeUrl: "/google-hangouts/",
      historyUrl: "/google-hangouts/history/",
      icon: <Users className="w-6 h-6" />,
      color: "bg-green-600/10 text-green-600"
    },
    {
      name: "Chalkup",
      tagline: "Classroom Collaboration",
      shutdownDate: "2018",
      shutdownYear: "2018",
      description: "An innovative classroom collaboration platform that connected teachers and students before being acquired by PowerSchool.",
      alternativeUrl: "/chalkup/",
      historyUrl: "/chalkup/history/",
      icon: <GraduationCap className="w-6 h-6" />,
      color: "bg-emerald-500/10 text-emerald-500"
    },
    {
      name: "TodaysMeet",
      tagline: "Backchannel Chat",
      shutdownDate: "June 29, 2018",
      shutdownYear: "2018",
      description: "A pioneering backchannel chat tool that powered classroom discussions in over 75 countries before GDPR compliance costs led to its shutdown.",
      alternativeUrl: "/todaysmeet/",
      historyUrl: "/todaysmeet/history/",
      icon: <MessageSquare className="w-6 h-6" />,
      color: "bg-pink-500/10 text-pink-500"
    },
    {
      name: "Wikispaces",
      tagline: "Education Wiki Platform",
      shutdownDate: "July 31, 2018",
      shutdownYear: "2018",
      description: "A pioneering wiki platform that powered over 20 million classroom projects before being shut down after 12 years of service.",
      alternativeUrl: "/wikispaces/",
      historyUrl: "/wikispaces/history/",
      icon: <BookOpen className="w-6 h-6" />,
      color: "bg-lime-500/10 text-lime-500"
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Discontinued Study & Productivity Platform Alternatives",
    "description": "Find free alternatives to discontinued study and productivity platforms including Groove, Studyverse, Edmodo, OpenStudy, Wunderlist, AltspaceVR, and Google Wave.",
    "url": "https://chillstudy.work/alternatives/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "about": {
      "@type": "ItemList",
      "itemListElement": platforms.map((platform, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": `${platform.name} Alternative`,
        "url": `https://chillstudy.work${platform.alternativeUrl}`
      }))
    },
    "keywords": "discontinued apps alternatives, Groove alternative, Studyverse alternative, Edmodo alternative, OpenStudy alternative, Wunderlist alternative, AltspaceVR alternative, Google Wave alternative"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Chill Study Work",
    "applicationCategory": "ProductivityApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1200",
      "bestRating": "5",
      "worstRating": "1"
    },
    "description": "Free alternative to discontinued study and productivity platforms. Offers virtual study rooms, Pomodoro timers, task management, and community features."
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Discontinued Platform Alternatives - Free Study & Productivity Tools | CSW</title>
        <meta name="description" content="Find free alternatives to discontinued study and productivity platforms including Groove, Studyverse, Edmodo, OpenStudy, Wunderlist, AltspaceVR, and Google Wave." />
        <meta name="keywords" content="discontinued apps alternatives, Groove alternative, Studyverse alternative, Edmodo alternative, OpenStudy alternative, Wunderlist alternative, AltspaceVR alternative, Google Wave alternative, HipChat alternative, Stride alternative, Sunrise Calendar alternative, Mailbox alternative, Yahoo Groups alternative, Google Spaces alternative, Quill alternative, Google Hangouts alternative, Chalkup alternative, TodaysMeet alternative, Wikispaces alternative" />
        <link rel="canonical" href="https://chillstudy.work/alternatives/" />
        <meta property="og:title" content="Discontinued Platform Alternatives - Free Study & Productivity Tools" />
        <meta property="og:description" content="Free alternatives to Groove, Studyverse, Edmodo, OpenStudy, Wunderlist, AltspaceVR, and Google Wave." />
        <meta property="og:url" content="https://chillstudy.work/alternatives/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Clock className="w-4 h-4" />
              18 Platforms • Free Alternatives Available
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Discontinued Platform Alternatives
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Your favorite study and productivity apps shut down? We've got you covered. CSW offers free alternatives 
              with <Link to="/study-with-me/" className="text-primary hover:underline">study rooms</Link>,{" "}
              <Link to="/study-timer/" className="text-primary hover:underline">timers</Link>, and{" "}
              <Link to="/task-management/" className="text-primary hover:underline">task management</Link>.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                Try CSW Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </section>

        {/* Platform Cards Grid */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 text-center">Platforms We've Replaced</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {platforms.map((platform) => (
                <article 
                  key={platform.name}
                  className="bg-card rounded-2xl p-6 border border-border/50 shadow-lg hover:shadow-xl transition-shadow flex flex-col"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-lg ${platform.color}`}>
                      {platform.icon}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                      <Calendar className="w-3 h-3" />
                      {platform.shutdownYear}
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-1">{platform.name}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{platform.tagline}</p>
                  
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4 flex-grow">
                    {platform.description}
                  </p>
                  
                  <div className="text-xs text-destructive mb-4 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Shut down: {platform.shutdownDate}
                  </div>
                  
                  <div className="flex gap-2 mt-auto">
                    <Link 
                      to={platform.alternativeUrl}
                      className="flex-1 text-center bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                      View Alternative
                    </Link>
                    <Link 
                      to={platform.historyUrl}
                      className="px-4 py-2 rounded-lg text-sm font-medium border border-border hover:bg-muted transition-colors"
                    >
                      History
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Why Platforms Shut Down */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
              <h2 className="text-2xl font-bold mb-6">Why Do Platforms Shut Down?</h2>
              <div className="grid sm:grid-cols-2 gap-6 text-muted-foreground">
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Business Challenges</h3>
                  <p className="text-sm">Many platforms struggle with monetization, user acquisition costs, or fail to achieve sustainable growth. Even with millions of users, profitability remains elusive.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Corporate Acquisitions</h3>
                  <p className="text-sm">When larger companies acquire apps (like Microsoft buying Wunderlist), they often sunset products to focus on their own solutions.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Shifting Priorities</h3>
                  <p className="text-sm">Companies may redirect resources to more profitable ventures, especially during economic downturns or strategic pivots.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Technical Debt</h3>
                  <p className="text-sm">Aging infrastructure becomes expensive to maintain. Some platforms choose shutdown over costly rebuilds.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CSW Features */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 text-center">What CSW Offers Instead</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/group-study/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors text-center">
                <Users className="w-8 h-8 mx-auto mb-2 text-primary" />
                <p className="font-semibold">Virtual Study Rooms</p>
                <p className="text-xs text-muted-foreground">Like Groove & Studyverse</p>
              </Link>
              <Link to="/task-management/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors text-center">
                <ListTodo className="w-8 h-8 mx-auto mb-2 text-primary" />
                <p className="font-semibold">Task Management</p>
                <p className="text-xs text-muted-foreground">Like Wunderlist</p>
              </Link>
              <Link to="/study-with-strangers/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors text-center">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-primary" />
                <p className="font-semibold">Community Features</p>
                <p className="text-xs text-muted-foreground">Like OpenStudy & Edmodo</p>
              </Link>
              <Link to="/online-workspace/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors text-center">
                <Waves className="w-8 h-8 mx-auto mb-2 text-primary" />
                <p className="font-semibold">Real-Time Collaboration</p>
                <p className="text-xs text-muted-foreground">Like Google Wave</p>
              </Link>
            </div>
          </div>
        </section>

        {/* Related Resources */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Learn More About Productivity</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/science-behind-study-with-me/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">The Science Behind Study With Me</p>
                <p className="text-sm text-muted-foreground">Why virtual study rooms actually work</p>
              </Link>
              <Link to="/blog/how-to-stop-procrastination/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">How to Stop Procrastination</p>
                <p className="text-sm text-muted-foreground">Science-backed strategies to get things done</p>
              </Link>
              <Link to="/blog/top-10-digital-tools-for-academic-success/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">Top 10 Digital Tools for Academic Success</p>
                <p className="text-sm text-muted-foreground">Essential tools for students</p>
              </Link>
              <Link to="/blog/apps-boost-organization-college/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">Apps to Boost Organization in College</p>
                <p className="text-sm text-muted-foreground">Stay organized and productive</p>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 text-center border border-primary/20">
            <h2 className="text-3xl font-bold mb-4">Ready to Find Your New Productivity Home?</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Join thousands of students and professionals who've made the switch to CSW — a stable, free platform built to last.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                Start Free Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Alternatives;
